import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Paperclip, X, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Notification sound (simple beep using Web Audio API)
let audioCtx = null;
export function playNotificationSound() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.3);
  } catch { /* */ }
}

// Typing indicator dots animation
export function TypingIndicator({ label }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className="w-2 h-2 rounded-full"
            style={{
              background: "var(--crm-accent)",
              opacity: 0.6,
              animation: `typingBounce 1.2s ease-in-out ${i * 0.15}s infinite`,
            }} />
        ))}
      </div>
      <span className="text-xs" style={{ color: "var(--crm-text-dim)" }}>{label} digitando...</span>
      <style>{`
        @keyframes typingBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

// Shared chat message bubble
export function ChatBubble({ msg, isOwn, ownerLabel }) {
  const isImage = msg.attachment && /\.(jpg|jpeg|png|gif|webp)$/i.test(msg.attachment);

  return (
    <div className={`flex mb-3 ${isOwn ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[80%] sm:max-w-[65%]">
        {msg.attachment && (
          <div className="mb-1">
            {isImage ? (
              <a href={`${BACKEND}${msg.attachment}`} target="_blank" rel="noopener noreferrer">
                <img src={`${BACKEND}${msg.attachment}`} alt={msg.attachment_name || "image"}
                  className="max-w-full max-h-48 rounded-xl object-cover" style={{ border: "1px solid var(--crm-border)" }} />
              </a>
            ) : (
              <a href={`${BACKEND}${msg.attachment}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium"
                style={{ background: "var(--crm-input-bg)", border: "1px solid var(--crm-border)", color: "var(--crm-accent)" }}>
                <FileText className="w-4 h-4" /> {msg.attachment_name || "Arquivo"}
              </a>
            )}
          </div>
        )}
        {msg.message && (
          <div className="px-4 py-2.5 rounded-2xl text-sm"
            style={{
              background: isOwn ? "var(--crm-accent)" : "var(--crm-surface)",
              color: isOwn ? "white" : "var(--crm-text)",
              border: !isOwn ? "1px solid var(--crm-border)" : "none",
              borderBottomRightRadius: isOwn ? "4px" : "16px",
              borderBottomLeftRadius: !isOwn ? "4px" : "16px",
            }}>
            {msg.message}
          </div>
        )}
        <p className={`text-[10px] mt-1 px-1 ${isOwn ? "text-right" : ""}`} style={{ color: "var(--crm-text-dim)" }}>
          {ownerLabel} · {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </div>
  );
}

// Shared chat input with file attachment + typing indicator
export function ChatInput({ onSend, placeholder, apiInstance, disabled, customerId }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const fileRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const sendTyping = useCallback(() => {
    if (customerId && apiInstance) {
      apiInstance.post(`/chat/${customerId}/typing`).catch(() => {});
    }
  }, [customerId, apiInstance]);

  const handleTextChange = (e) => {
    setText(e.target.value);
    sendTyping();
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {}, 3000);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiInstance.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({ url: data.url, name: file.name });
    } catch { /* */ }
    setSending(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !attachment) return;
    setSending(true);
    try {
      await onSend({
        message: text.trim() || (attachment ? attachment.name : ""),
        attachment: attachment?.url || null,
        attachment_name: attachment?.name || null,
      });
      setText("");
      setAttachment(null);
    } catch { /* */ }
    setSending(false);
  };

  return (
    <div style={{ borderTop: "1px solid var(--crm-border)" }}>
      {attachment && (
        <div className="px-4 pt-3 flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
            style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
            <Paperclip className="w-3 h-3" /> {attachment.name}
          </div>
          <button onClick={() => setAttachment(null)} className="p-1 rounded" style={{ color: "var(--crm-text-dim)" }}>
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <form onSubmit={handleSubmit} className="p-4 flex gap-3">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={sending || disabled}
          className="p-2.5 rounded-xl transition-colors flex-shrink-0"
          style={{ color: "var(--crm-text-muted)", background: "var(--crm-input-bg)", border: "1px solid var(--crm-border)" }}>
          <Paperclip className="w-4 h-4" />
        </button>
        <input value={text} onChange={handleTextChange}
          className="crm-input flex-1" placeholder={placeholder || "Digite sua mensagem..."} disabled={disabled} />
        <button type="submit" disabled={sending || disabled || (!text.trim() && !attachment)}
          className="crm-btn-primary px-4" style={{ flexShrink: 0 }}>
          <Send className="w-4 h-4" />
        </button>
      </form>
      <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt" className="hidden"
        onChange={(e) => handleFile(e.target.files[0])} />
    </div>
  );
}

// Hook for typing indicator polling
export function useTypingIndicator(apiInstance, customerId) {
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (!customerId || !apiInstance) return;
    const check = () => {
      apiInstance.get(`/chat/${customerId}/typing`).then((r) => {
        setIsTyping(r.data.typing);
      }).catch(() => {});
    };
    const iv = setInterval(check, 2000);
    return () => clearInterval(iv);
  }, [customerId, apiInstance]);

  return isTyping;
}
